import { useEffect, useState, useMemo, useCallback } from 'react';
import { getTrainingAdminPolicies, GetTrainingAdminPoliciesOutputType, getPolicyDetail, GetPolicyDetailOutputType, savePolicyAdmin, publishPolicy, assignPolicy, getPolicyReport, GetPolicyReportOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Search, Plus, Shield, Upload, ChevronLeft, AlertTriangle, Clock, CheckCircle2, Eye, Send, FileText, Archive, Download, Users, BarChart3, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import PolicyListView from './PolicyListView';
import PolicyEditorView from './PolicyEditorView';
import PolicyDetailView from './PolicyDetailView';

type Policy = GetTrainingAdminPoliciesOutputType['policies'][0];
type Category = GetTrainingAdminPoliciesOutputType['categories'][0];

export default function PolicyAdminSection() {
  const [tab, setTab] = useState<'list' | 'editor' | 'detail'>('list');
  const [editingPolicyId, setEditingPolicyId] = useState<string | undefined>();
  const [detailPolicyId, setDetailPolicyId] = useState<string | undefined>();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stats, setStats] = useState<GetTrainingAdminPoliciesOutputType['stats'] | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getTrainingAdminPolicies({ includeArchived: true });
      setPolicies(d.policies);
      setCategories(d.categories);
      setStats(d.stats);
    } catch { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const openEditor = (id?: string) => { setEditingPolicyId(id); setTab('editor'); };
  const openDetail = (id: string) => { setDetailPolicyId(id); setTab('detail'); };
  const backToList = () => { setTab('list'); setEditingPolicyId(undefined); setDetailPolicyId(undefined); loadPolicies(); };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      {tab === 'list' && (
        <PolicyListView
          policies={policies}
          categories={categories}
          stats={stats}
          onNewPolicy={() => openEditor()}
          onEditPolicy={openEditor}
          onViewPolicy={openDetail}
          onRefresh={loadPolicies}
        />
      )}
      {tab === 'editor' && (
        <PolicyEditorView
          policyId={editingPolicyId}
          categories={categories}
          onBack={backToList}
          onSaved={(id) => { setEditingPolicyId(id); openDetail(id); }}
        />
      )}
      {tab === 'detail' && detailPolicyId && (
        <PolicyDetailView
          policyId={detailPolicyId}
          onBack={backToList}
          onEdit={(id) => openEditor(id)}
          onRefresh={loadPolicies}
        />
      )}
    </div>
  );
}
